import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../../contexts/Web3Context';
import { 
  CurrencyDollarIcon,
  TrophyIcon,
  GiftIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon 
} from '@heroicons/react/24/outline';

interface TokenReward {
  id: string;
  type: 'creation' | 'collaboration' | 'voting' | 'staking' | 'referral';
  amount: string;
  description: string;
  timestamp: number;
  status: 'pending' | 'claimed' | 'expired';
}

interface StakingPool {
  id: string;
  name: string;
  apy: number;
  totalStaked: string;
  userStaked: string;
  rewards: string;
  lockPeriod: number; // days
  status: 'active' | 'ended';
}

const TokenEconomy: React.FC = () => {
  const { wallet, tokenBalances } = useWeb3();
  const [rewards, setRewards] = useState<TokenReward[]>([]);
  const [stakingPools, setStakingPools] = useState<StakingPool[]>([]);
  const [selectedPool, setSelectedPool] = useState<StakingPool | null>(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (wallet?.address) {
      loadTokenEconomyData();
    }
  }, [wallet]);

  const loadTokenEconomyData = async () => {
    setLoading(true);
    try {
      // Mock rewards data
      const mockRewards: TokenReward[] = [
        {
          id: 'reward_1',
          type: 'creation',
          amount: '50',
          description: '发布原创音乐作品《Sunset Dreams》',
          timestamp: Date.now() - 86400000,
          status: 'claimed'
        },
        {
          id: 'reward_2',
          type: 'collaboration',
          amount: '25',
          description: '参与协作项目获得奖励',
          timestamp: Date.now() - 3600000,
          status: 'pending'
        },
        {
          id: 'reward_3',
          type: 'voting',
          amount: '10',
          description: 'DAO治理投票参与奖励',
          timestamp: Date.now() - 1800000,
          status: 'pending'
        }
      ];

      // Mock staking pools
      const mockPools: StakingPool[] = [
        {
          id: 'pool_1',
          name: '创作者质押池',
          apy: 12.5,
          totalStaked: '1,250,000',
          userStaked: '500',
          rewards: '15.2',
          lockPeriod: 30,
          status: 'active'
        },
        {
          id: 'pool_2',
          name: '治理代币池',
          apy: 8.0,
          totalStaked: '2,100,000',
          userStaked: '0',
          rewards: '0',
          lockPeriod: 90,
          status: 'active'
        }
      ];

      setRewards(mockRewards);
      setStakingPools(mockPools);
    } catch (error) {
      console.error('Failed to load token economy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimReward = async (rewardId: string) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setRewards(prev => prev.map(r => 
        r.id === rewardId ? { ...r, status: 'claimed' as const } : r
      ));
      
      alert('奖励已成功领取！');
    } catch (error) {
      console.error('Failed to claim reward:', error);
      alert('领取失败，请重试');
    }
  };

  const handleStake = async (poolId: string) => {
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) {
      alert('请输入有效的质押金额');
      return;
    }

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setStakingPools(prev => prev.map(p => 
        p.id === poolId 
          ? { 
              ...p, 
              userStaked: (parseFloat(p.userStaked) + parseFloat(stakeAmount)).toString(),
              totalStaked: (parseFloat(p.totalStaked.replace(/,/g, '')) + parseFloat(stakeAmount)).toLocaleString()
            }
          : p
      ));
      
      setStakeAmount('');
      setSelectedPool(null);
      alert('质押成功！');
    } catch (error) {
      console.error('Failed to stake:', error);
      alert('质押失败，请重试');
    }
  };

  const getRewardTypeIcon = (type: TokenReward['type']) => {
    switch (type) {
      case 'creation':
        return <TrophyIcon className="w-5 h-5 text-yellow-500" />;
      case 'collaboration':
        return <GiftIcon className="w-5 h-5 text-blue-500" />;
      case 'voting':
        return <ChartBarIcon className="w-5 h-5 text-green-500" />;
      case 'staking':
        return <CurrencyDollarIcon className="w-5 h-5 text-purple-500" />;
      case 'referral':
        return <ArrowUpIcon className="w-5 h-5 text-orange-500" />;
    }
  };

  const getRewardTypeName = (type: TokenReward['type']) => {
    switch (type) {
      case 'creation': return '创作奖励';
      case 'collaboration': return '协作奖励';
      case 'voting': return '投票奖励';
      case 'staking': return '质押奖励';
      case 'referral': return '推荐奖励';
    }
  };

  const ibomBalance = tokenBalances.find(t => t.symbol === 'IBOM')?.balance || '0';
  const totalPendingRewards = rewards
    .filter(r => r.status === 'pending')
    .reduce((sum, r) => sum + parseFloat(r.amount), 0);

  if (!wallet?.isConnected) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">请先连接钱包以查看代币经济</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">代币经济</h1>
        <p className="text-gray-600">管理 IBOM 代币奖励和质押</p>
        
        {/* 代币经济核心价值展示 */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="font-semibold text-amber-800 mb-2">🎵 创作激励</h3>
            <p className="text-sm text-amber-700">优质音乐创作获得代币奖励，激励创作者持续产出高质量作品。</p>
          </div>
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <h3 className="font-semibold text-teal-800 mb-2">💎 质押收益</h3>
            <p className="text-sm text-teal-700">质押代币获得稳定收益，支持平台发展的同时享受生态增长红利。</p>
          </div>
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
            <h3 className="font-semibold text-violet-800 mb-2">🏆 治理权益</h3>
            <p className="text-sm text-violet-700">持有代币获得平台治理投票权，参与重要决策制定。</p>
          </div>
        </div>
      </div>

      {/* 代币概览 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <CurrencyDollarIcon className="w-8 h-8 text-blue-500" />
            <div>
              <h3 className="text-sm text-gray-500">IBOM余额</h3>
              <p className="text-2xl font-bold text-blue-600">{parseFloat(ibomBalance).toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <GiftIcon className="w-8 h-8 text-green-500" />
            <div>
              <h3 className="text-sm text-gray-500">待领取奖励</h3>
              <p className="text-2xl font-bold text-green-600">{totalPendingRewards}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <TrophyIcon className="w-8 h-8 text-yellow-500" />
            <div>
              <h3 className="text-sm text-gray-500">质押收益</h3>
              <p className="text-2xl font-bold text-yellow-600">
                {stakingPools.reduce((sum, p) => sum + parseFloat(p.rewards), 0).toFixed(1)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <ArrowUpIcon className="w-8 h-8 text-purple-500" />
            <div>
              <h3 className="text-sm text-gray-500">总质押</h3>
              <p className="text-2xl font-bold text-purple-600">
                {stakingPools.reduce((sum, p) => sum + parseFloat(p.userStaked), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 奖励中心 */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">奖励中心</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : rewards.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              暂无奖励记录
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {rewards.map((reward) => (
                <div key={reward.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {getRewardTypeIcon(reward.type)}
                      <div>
                        <h3 className="font-medium">{getRewardTypeName(reward.type)}</h3>
                        <p className="text-sm text-gray-600 mt-1">{reward.description}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(reward.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg text-green-600">
                        +{reward.amount} IBOM
                      </div>
                      {reward.status === 'pending' ? (
                        <button
                          onClick={() => handleClaimReward(reward.id)}
                          className="mt-2 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          领取
                        </button>
                      ) : reward.status === 'claimed' ? (
                        <span className="text-xs text-gray-500">已领取</span>
                      ) : (
                        <span className="text-xs text-red-500">已过期</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 质押池 */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">质押池</h2>
          </div>
          
          <div className="p-6 space-y-4">
            {stakingPools.map((pool) => (
              <div key={pool.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">{pool.name}</h3>
                  <span className="text-sm bg-green-100 text-green-600 px-2 py-1 rounded">
                    APY {pool.apy}%
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                  <div>
                    <span className="text-gray-500">总质押:</span>
                    <p className="font-medium">{pool.totalStaked} IBOM</p>
                  </div>
                  <div>
                    <span className="text-gray-500">锁定期:</span>
                    <p className="font-medium">{pool.lockPeriod} 天</p>
                  </div>
                  <div>
                    <span className="text-gray-500">我的质押:</span>
                    <p className="font-medium">{pool.userStaked} IBOM</p>
                  </div>
                  <div>
                    <span className="text-gray-500">待领取收益:</span>
                    <p className="font-medium text-green-600">{pool.rewards} IBOM</p>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedPool(pool)}
                    className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 text-sm"
                  >
                    质押
                  </button>
                  {parseFloat(pool.rewards) > 0 && (
                    <button className="px-4 bg-green-600 text-white py-2 rounded hover:bg-green-700 text-sm">
                      领取收益
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 质押模态框 */}
      {selectedPool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">质押到 {selectedPool.name}</h2>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-2">质押详情:</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>年化收益率:</span>
                  <span className="font-medium text-green-600">{selectedPool.apy}%</span>
                </div>
                <div className="flex justify-between">
                  <span>锁定期:</span>
                  <span>{selectedPool.lockPeriod} 天</span>
                </div>
                <div className="flex justify-between">
                  <span>可用余额:</span>
                  <span>{ibomBalance} IBOM</span>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">质押数量</label>
              <input
                type="number"
                value={stakeAmount}
                onChange={(e) => setStakeAmount(e.target.value)}
                placeholder="输入质押数量"
                max={ibomBalance}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => handleStake(selectedPool.id)}
                disabled={!stakeAmount || parseFloat(stakeAmount) <= 0}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                确认质押
              </button>
              <button
                onClick={() => {
                  setSelectedPool(null);
                  setStakeAmount('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenEconomy;
